import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DefaultFocusIndicator, FocusIndicatorInsetWidth, FocusIndicatorWidth } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TextEllipsis } from '../../styles/TextEllipsis';

// Only the active tab should be visible, all others should be hidden
export const Visible: React.CSSProperties = { visibility: 'visible' };
export const Hidden: React.CSSProperties = { visibility: 'hidden' };

const TabIndicatorHeight = 3;

export const TabsWidgetDimensions = {
  tabLabelPadding: 24,
  textOnlyTabListHeight: 39 + TabIndicatorHeight,
  iconAndTextTabListHeight: 51 + TabIndicatorHeight,
  tabIndicatorHeight: TabIndicatorHeight,
};

const FocusIndicatorOffset = FocusIndicatorWidth + FocusIndicatorInsetWidth;

const BackgroundColors = {
  Active: '#c9c9c9',
  Default: 'transparent',
  Hover: '#e5e5e5',
};

const IndicatorColors = {
  Active: '#2e2e2e',
  Default: '#c9c9c9',
  Hover: '#444',
  Selected: '#5c5c5c',
};

// The client specifies which tab panel we should anchor the tab widget height to. It's often the tallest tab panel,
// so that the dialog will always be tall enough and will not resize when switching tabs
export const NonAnchorTabPanelStyles: React.CSSProperties = {
  position: 'absolute',
  width: '100%',
  left: '0',
  top: '0'
};

export const RootStyleName = TypeStyle.style({
  display: 'flex',
  flexDirection: 'column',
});

export const TabPanelScrollContainerStyles = (padding?: React.CSSProperties['padding']): React.CSSProperties => ({
  overflowY: 'auto',
  padding: padding ?? 18,
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
});

export const TabPanelContainerStyles: React.CSSProperties = {
  position: 'relative',
};

export const GetTabListStyles = (isAnIconDefinedInTabs: boolean, tabHeightOverride?: React.CSSProperties['height']): React.CSSProperties => {
  return {
    display: 'flex',
    flexShrink: 0,
    fontSize: 12,
    height: tabHeightOverride ?? (isAnIconDefinedInTabs ? TabsWidgetDimensions.iconAndTextTabListHeight : TabsWidgetDimensions.textOnlyTabListHeight),
    ...PreventUserSelect,
  };
};

export const DefaultTabLabelStyleName: string = TypeStyle.style({
  color: '#2e2e2e',
  width: '100%',
  ...TextEllipsis
});

export const TabStyleName = TypeStyle.style({
  backgroundColor: BackgroundColors.Default,
  boxSizing: 'border-box',
  display: 'inline-block',
  height: '100%', // TFSID 773048
  overflow: 'hidden',
  position: 'relative',
  $nest: {
    '&:hover': { backgroundColor: BackgroundColors.Hover },
    '&:active': { backgroundColor: BackgroundColors.Active },

    '&::after': { // indicator
      backgroundColor: IndicatorColors.Default,
      content: `""`,
      height: TabsWidgetDimensions.tabIndicatorHeight,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    '&:hover::after': { backgroundColor: IndicatorColors.Hover },
    '&[aria-selected="true"]::after': { backgroundColor: IndicatorColors.Selected },
    '&:active::after': { backgroundColor: IndicatorColors.Active },

    // Everything except for the bottom outline of the focus indicator should be "inset".
    // The bottom outline should be offset with a 1px gap.
    // Since there is no way to set outline-offset for just the bottom, need to hack this
    // behavior by increasing the height and shifting the tab indicator upwards
    '&:focus-visible': {
      ...DefaultFocusIndicator(),
      borderRadius: 2,
      height: `calc(100% + ${FocusIndicatorOffset}px)`
    },
    [`&:focus-visible .${DefaultTabLabelStyleName}`]: {
      marginTop: -FocusIndicatorOffset, // adjusting the height of the tab causes the text to shift
    },
    '&:focus-visible::after': {
      bottom: FocusIndicatorOffset,
      left: FocusIndicatorOffset,
      right: FocusIndicatorOffset,
    },
  }
});

export const GetTabLabelContainerStyles = (horizontalPadding?: React.CSSProperties['paddingLeft']): React.CSSProperties => {
  return {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    paddingRight: horizontalPadding ?? TabsWidgetDimensions.tabLabelPadding,
    paddingLeft: horizontalPadding ?? TabsWidgetDimensions.tabLabelPadding,
  };
};



export const TabContentStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  justifyContent: 'center'
};

export const LabelStyle: React.CSSProperties = { display: 'block', ...TextEllipsis };
